import React, { useState } from 'react';
import { formatDublinDateTime, formatDublinDate } from '../../utils/date';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetFireDrillQuery,
  useResolveFireDrillIssueMutation,
  useWithdrawFireDrillMutation,
} from '../../store/api/localApi';
import {
  FireDrillStatus,
  FireDrillScope,
  FireDrillType,
  FireDrillIssueCategory,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink, useScopedPath } from '../../components/ScopedLink';
import { useToast } from '../../components/Toast';

// ─── helpers ─────────────────────────────────────────────────────────────────

function labelForType(type: FireDrillType): string {
  switch (type) {
    case FireDrillType.ANNOUNCED:
      return 'Announced';
    case FireDrillType.UNANNOUNCED:
      return 'Unannounced';
    case FireDrillType.EVAC_REAL:
      return 'Real evac';
  }
}

function labelForIssueCategory(cat: FireDrillIssueCategory): string {
  switch (cat) {
    case FireDrillIssueCategory.DOOR:
      return 'Door';
    case FireDrillIssueCategory.ALARM:
      return 'Alarm';
    case FireDrillIssueCategory.ROUTE:
      return 'Route';
    case FireDrillIssueCategory.STAFF:
      return 'Staff';
    case FireDrillIssueCategory.CHILD:
      return 'Child';
    case FireDrillIssueCategory.EQUIPMENT:
      return 'Equipment';
    case FireDrillIssueCategory.OTHER:
      return 'Other';
  }
}

function labelForRole(role: string): string {
  switch (role) {
    case 'STAFF':
      return 'Staff';
    case 'LOCATION_ADMIN':
      return 'Location admin';
    case 'ORG_ADMIN':
      return 'Org admin';
    case 'SUPER_ADMIN':
      return 'Super admin';
    default:
      return role;
  }
}

function formatEvacuationTime(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// Drill records are read by inspectors against the location's wall clock —
// Europe/Dublin, never UTC (an hour off all summer) and never the viewer's.
function formatDateTime(isoString: string): string {
  return formatDublinDateTime(isoString);
}

function formatDate(isoString: string): string {
  return formatDublinDate(isoString);
}

// ─── StatusPill ───────────────────────────────────────────────────────────────

interface StatusPillProps {
  status: FireDrillStatus;
}

const StatusPill: React.FC<StatusPillProps> = ({ status }) => {
  const config: Record<FireDrillStatus, { label: string; classes: string }> = {
    [FireDrillStatus.IN_PROGRESS]: {
      label: 'In progress',
      classes: 'bg-amber-100 text-amber-800',
    },
    [FireDrillStatus.COMPLETED]: {
      label: 'Completed',
      classes: 'bg-emerald-100 text-emerald-800',
    },
    [FireDrillStatus.WITHDRAWN]: {
      label: 'Withdrawn',
      classes: 'bg-gray-100 text-gray-600',
    },
  };

  const { label, classes } = config[status];

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${classes}`}
    >
      {label}
    </span>
  );
};

// ─── ConfirmModal ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  description,
  confirmLabel,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-2 text-sm text-gray-600">{description}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
          >
            {isLoading ? 'Withdrawing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <h2 className="px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-100 bg-gray-50">
      {title}
    </h2>
    <div className="px-4 py-4">{children}</div>
  </section>
);

// ─── FireDrillDetailPage ──────────────────────────────────────────────────────

const FireDrillDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scopedPath = useScopedPath();
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const { data, isLoading, isError, refetch } = useGetFireDrillQuery(id!, { skip: !id });
  const drill = data?.drill;
  const audit = data?.audit;

  const [resolveIssue, { isLoading: isResolving }] = useResolveFireDrillIssueMutation();
  const [withdrawDrill, { isLoading: isWithdrawing }] = useWithdrawFireDrillMutation();

  // Per-issue resolution note state — keyed by issueId
  const [resolutionNotes, setResolutionNotes] = useState<Record<string, string>>({});

  // Withdraw confirmation modal
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  // ── Permission logic ───────────────────────────────────────────────────────
  const isRecorder = drill?.startedBy === user?.id;
  const canSeeAudit = !!audit;
  const canWithdraw = (isSuperAdmin || isAdmin) && drill?.status !== FireDrillStatus.WITHDRAWN;
  const canResolveIssues = true; // any staff with access

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleResolve = async (issueId: string) => {
    const note = resolutionNotes[issueId]?.trim();
    if (!note) return;
    try {
      await resolveIssue({ drillId: id!, issueId, resolutionNote: note }).unwrap();
      setResolutionNotes((prev) => {
        const next = { ...prev };
        delete next[issueId];
        return next;
      });
      showSuccess('Issue resolved');
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to resolve');
    }
  };

  const handleWithdrawConfirm = async () => {
    try {
      await withdrawDrill(id!).unwrap();
      showSuccess('Drill withdrawn');
      navigate(scopedPath('/fire-drills'));
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to withdraw');
      setShowWithdrawConfirm(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError || !drill) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">
          {isError ? 'Failed to load drill.' : 'Drill not found.'}
        </p>
        {isError && (
          <button
            type="button"
            onClick={() => refetch()}
            className="text-primary-600 hover:text-primary-500 underline"
          >
            Try again
          </button>
        )}
        <ScopedLink to="/fire-drills" className="text-primary-600 hover:text-primary-500 underline">
          ← Back to fire drills
        </ScopedLink>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  const accountedFor = drill.attendees.filter((a) => a.accountedFor).length;
  const totalAttendees = drill.attendees.length;

  const scopeLabel =
    drill.scope === FireDrillScope.LOCATION
      ? 'Location'
      : drill.className
      ? `Class: ${drill.className}`
      : 'Class';

  const openIssues = drill.issues.filter((i) => !i.resolvedAt);
  const closedIssues = drill.issues.filter((i) => i.resolvedAt);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div data-testid="fire-drill-detail" className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Back link */}
      <ScopedLink
        data-testid="fire-drill-detail-back"
        to="/fire-drills"
        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-500"
      >
        ← Fire drill log
      </ScopedLink>

      {/* Withdrawn banner */}
      {drill.status === FireDrillStatus.WITHDRAWN && drill.withdrawnAt && (
        <div className="rounded-md bg-gray-100 border border-gray-300 px-4 py-3 text-sm text-gray-700">
          <span className="font-medium">Withdrawn</span> at {formatDateTime(drill.withdrawnAt)}
          {drill.withdrawnBy && audit?.withdrawnBy && <> by {audit.withdrawnBy}</>}
        </div>
      )}

      {/* Header section */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-5">
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-gray-900">
              Fire Drill — {formatDateTime(drill.startedAt)}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <span>{labelForType(drill.type)}</span>
              <span className="text-gray-300">·</span>
              <span>{scopeLabel}</span>
              <span className="text-gray-300">·</span>
              <span>Recorded by {drill.startedByName}</span>
            </div>
          </div>
          <StatusPill status={drill.status} />
        </div>
      </div>

      {/* Drill stats */}
      <Section title="Drill summary">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-gray-500">Evacuation time</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900">
              {formatEvacuationTime(drill.evacuationSeconds)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Attendees accounted</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900">
              {accountedFor} / {totalAttendees}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Assembly point</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900">
              {drill.assemblyPoint || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Exit route</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900">{drill.exitRoute || '—'}</dd>
          </div>
          {drill.scenario && (
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-xs text-gray-500">Scenario</dt>
              <dd className="mt-0.5 text-sm text-gray-900">{drill.scenario}</dd>
            </div>
          )}
        </dl>
      </Section>

      {/* Attendees */}
      {drill.attendees.length > 0 && (
        <Section title={`Attendees (${totalAttendees})`}>
          <ul className="divide-y divide-gray-100">
            {drill.attendees.map((attendee) => (
              <li
                key={attendee.studentId}
                data-testid={`fire-drill-detail-attendee-${attendee.studentId}`}
                className="flex items-center justify-between py-2 text-sm"
              >
                <span className="font-medium text-gray-900">{attendee.studentName}</span>
                {attendee.accountedFor ? (
                  <span className="text-emerald-700">Present</span>
                ) : (
                  <span className="text-amber-700">
                    Not accounted
                    {attendee.unaccountedReason ? `: ${attendee.unaccountedReason}` : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Staff */}
      {drill.staff.length > 0 && (
        <Section title={`Staff present (${drill.staff.length})`}>
          <ul className="divide-y divide-gray-100">
            {drill.staff.map((member) => (
              <li key={member.userId} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-gray-900">{member.userName}</span>
                <span className="text-gray-500">{labelForRole(member.role)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Issues */}
      {drill.issues.length > 0 && (
        <Section title={`Issues (${drill.issues.length})`}>
          <div className="space-y-4">
            {/* Open issues */}
            {openIssues.map((issue) => (
              <div
                key={issue.id}
                data-testid={`fire-drill-detail-issue-${issue.id}`}
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3"
              >
                <div className="flex items-start gap-2">
                  <span className="inline-block text-xs font-medium bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 mt-0.5 shrink-0">
                    {labelForIssueCategory(issue.category)}
                  </span>
                  <p className="flex-1 text-sm text-gray-800">{issue.description}</p>
                </div>

                {canResolveIssues && (
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="text"
                      data-testid={`fire-drill-detail-issue-${issue.id}-resolution-note`}
                      value={resolutionNotes[issue.id] ?? ''}
                      onChange={(e) =>
                        setResolutionNotes((prev) => ({ ...prev, [issue.id]: e.target.value }))
                      }
                      placeholder="Resolution note…"
                      className="flex-1 rounded-md border-gray-300 text-sm"
                    />
                    <button
                      type="button"
                      data-testid={`fire-drill-detail-issue-${issue.id}-resolve`}
                      onClick={() => handleResolve(issue.id)}
                      disabled={isResolving || !resolutionNotes[issue.id]?.trim()}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isResolving ? 'Saving…' : 'Mark resolved'}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Resolved issues */}
            {closedIssues.map((issue) => (
              <div
                key={issue.id}
                data-testid={`fire-drill-detail-issue-${issue.id}`}
                className="rounded-md border border-gray-200 bg-white px-3 py-3"
              >
                <div className="flex items-start gap-2">
                  <span className="inline-block text-xs font-medium bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 mt-0.5 shrink-0">
                    {labelForIssueCategory(issue.category)}
                  </span>
                  <p className="flex-1 text-sm text-gray-500 line-through">{issue.description}</p>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Resolved by {issue.resolvedByName || 'unknown'} on{' '}
                  {issue.resolvedAt ? formatDate(issue.resolvedAt) : '—'}
                  {issue.resolutionNote && (
                    <>
                      : <span className="text-gray-700">{issue.resolutionNote}</span>
                    </>
                  )}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* No issues */}
      {drill.issues.length === 0 && (
        <Section title="Issues">
          <p className="text-sm text-gray-400">No issues recorded.</p>
        </Section>
      )}

      {/* Notes */}
      {drill.notes && (
        <Section title="Notes">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{drill.notes}</p>
        </Section>
      )}

      {/* Audit timeline — admin or recorder only */}
      {canSeeAudit &&
        (isAdmin || isSuperAdmin || isRecorder) &&
        audit.edits &&
        audit.edits.length > 0 && (
          <Section title="Audit timeline">
            <ol className="space-y-3">
              {audit.edits.map((entry, index) => (
                <li key={index} className="flex gap-3 text-sm">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-2 w-2 rounded-full bg-gray-300 mt-1.5" />
                  </div>
                  <div>
                    <p className="text-gray-700">
                      <span className="font-medium">{entry.editedByName}</span> edited{' '}
                      {entry.fields.join(', ')}
                    </p>
                    <p className="text-xs text-gray-400">{formatDateTime(entry.editedAt)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

      {/* Admin actions */}
      {canWithdraw && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            data-testid="fire-drill-detail-withdraw"
            onClick={() => setShowWithdrawConfirm(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
          >
            Withdraw drill
          </button>
        </div>
      )}

      {/* Withdraw confirm modal */}
      <ConfirmModal
        open={showWithdrawConfirm}
        title="Withdraw this drill?"
        description="This will mark the drill as withdrawn. This action cannot be undone."
        confirmLabel="Withdraw drill"
        isLoading={isWithdrawing}
        onConfirm={handleWithdrawConfirm}
        onCancel={() => setShowWithdrawConfirm(false)}
      />
    </div>
  );
};

export default FireDrillDetailPage;
